import { useCallback, useEffect, useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import {
    Badge,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    IconButton,
    LinearProgress,
    Typography,
} from '@material-ui/core';
import { Delete, Edit, GetApp, ArrowForward, Warning } from '@material-ui/icons';
import { groupBy, sortBy } from 'lodash';

import { ContentCopy, MaterialUiIcon } from '../../icon';
import Link from '../../links/Link';
import TestTypeDeleteDialog from './TestTypeDeleteDialog';
import { AdminPages, buildRootPageTarget } from '../AdminLinkUtils';
import { useDataService } from '../../../services/ApiDataService';
import { useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { useIsAdminUser } from '../../../stores/SessionStore';
import { useAssignLocation } from '../../../utilities/PgmUtilities';
import { getTestCounts, getAllTestTypes } from './TestTypesApi';
import { ADMIN_TEST_TYPES_URL } from './TestTypesAdminConstants';
import { useTestTypesListPanelStyles } from './TestTypesListPanelStyles';

const REMOVE_STATUS = 'Deleted';

function sortTestTypes(testTypes) {
    if (!testTypes || testTypes.length < 1) {
        return [];
    }

    const nativeTests = [];
    const builtInTests = [];
    const customTests = [];

    testTypes.forEach((testType) => {
        if (testType?.isNativeSort) {
            nativeTests.push(testType);
        } else if (testType?.builtIn === true) {
            builtInTests.push(testType);
        } else {
            customTests.push(testType);
        }
    });

    const sortByTitle = (list) => sortBy(list, (item) => `${item.details.title}`.toLowerCase());

    return [...sortByTitle(nativeTests).reverse(), ...sortByTitle(builtInTests), ...sortByTitle(customTests)];
}

/**
 * Groups sorted testTypes by 'group' field, preserving sort order.
 * Mirrors portal/ui template: list.testTypes | groupBy:'group'
 * Counts are looked up separately via groupCounts[group] (like portal/ui's list.groupCounts[group]).
 */
function groupTestTypes(testTypes) {
    const grouped = groupBy(testTypes, 'group');

    // Preserve the sort order from sortTestTypes by collecting groups in first-appearance order
    const orderedGroups = [];
    const seen = new Set();
    testTypes.forEach((tt) => {
        const g = tt?.group;
        if (g && !seen.has(g)) {
            seen.add(g);
            orderedGroups.push(g);
        }
    });

    return orderedGroups
        .map((group) => ({
            group,
            testTypes: (grouped[group] || []).filter((item) => item?.hidden !== true),
        }))
        .filter((entry) => entry.testTypes.length > 0);
}

function buildCardTitle(testTypes, groupName) {
    if (testTypes.length === 1) {
        const title = testTypes[0]?.details?.title || testTypes[0]?.name || groupName;
        return `${title} Tests`;
    }

    return `${groupName} Tests`;
}

function buildDescription(testTypes, groupName) {
    if (groupName === 'VoIP') {
        return t`VoIP Tests to monitor a location's connectivity and call quality to both internal and external telephony targets using nPoints/Edge Sensors.`;
    }
    if (groupName === 'Web') {
        return t`Use Web Tests to monitor internal or external web applications, such as intranets, portals, or SaaS applications.`;
    }
    return testTypes
        .map((item) => item?.details?.description)
        .filter(Boolean)
        .join('\n\n');
}

function buildGotoServiceTestsUrl(group) {
    const baseUrl = buildRootPageTarget(AdminPages.ServiceTests);
    const encodedGroup = encodeURIComponent(group || '');
    const legacyPath = encodedGroup ? `${baseUrl}/${encodedGroup}/tests` : `${baseUrl}/tests`;
    const params = new URLSearchParams();
    params.set('pageState', 'tests-list');

    const query = params.toString();
    return query ? `${legacyPath}?${query}` : legacyPath;
}

function canExport(isAdminUser, testTypes = []) {
    const item = testTypes[0];
    return isAdminUser && testTypes.length === 1 && !item?.isNativeSort && !item?.builtIn;
}

function canCopy(isAdminUser, testTypes = []) {
    const item = testTypes[0];
    return isAdminUser && testTypes.length === 1 && !item?.isNativeSort && !item?.doNotCopy;
}

function canDelete(isAdminUser, testTypes = []) {
    const item = testTypes[0];
    return isAdminUser && testTypes.length === 1 && !item?.isNativeSort && !item?.builtIn;
}

function canEdit(isAdminUser, testTypes = []) {
    const item = testTypes[0];
    return isAdminUser && testTypes.length === 1 && !item?.isNativeSort && !item?.builtIn;
}

function ActionIconButton({ className, to, onClick, label, children }) {
    const linkProps = to
        ? {
              component: Link,
              to,
          }
        : {};

    return (
        <IconButton
            className={className}
            color="primary"
            size="small"
            onClick={onClick}
            aria-label={label}
            title={label}
            {...linkProps}
        >
            {children}
        </IconButton>
    );
}

export default function TestTypesListPanel() {
    const classes = useTestTypesListPanelStyles();
    const dataService = useDataService();
    const [isAdminUser] = useIsAdminUser();
    const assignLocation = useAssignLocation();
    const { deleteWrapper } = useFormDeleteWrapper();
    const [testTypes, setTestTypes] = useState([]);
    const [groupCounts, setGroupCounts] = useState({});
    const [initialized, setInitialized] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);

    const load = useCallback(async () => {
        const [testTypeData, testCountData] = await Promise.all([
            getAllTestTypes(dataService),
            getTestCounts(dataService),
        ]);
        setTestTypes(sortTestTypes(testTypeData));
        setGroupCounts(testCountData || {});
    }, [dataService]);

    useEffect(() => {
        let ignore = false;
        setInitialized(false);

        const loadData = async () => {
            try {
                await load();
                if (!ignore) {
                    // no-op
                }
            } catch (error) {
                if (!ignore) {
                    setTestTypes([]);
                    setGroupCounts({});
                }
                // eslint-disable-next-line no-console
                console.error('Failed to load test types', error);
            } finally {
                if (!ignore) {
                    setInitialized(true);
                }
            }
        };

        loadData();

        return () => {
            ignore = true;
        };
    }, [load]);

    const handleDelete = useCallback((testTypeId) => {
        if (!testTypeId) {
            return;
        }
        setPendingDeleteId(testTypeId);
        setDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        if (!pendingDeleteId) {
            return undefined;
        }
        return deleteWrapper({
            deleteFunction: () =>
                dataService.put(`${ADMIN_TEST_TYPES_URL}/${pendingDeleteId}`, { status: REMOVE_STATUS }),
            refresherFunc: load,
        })();
    }, [pendingDeleteId, deleteWrapper, dataService, load]);

    const handleExport = useCallback(
        (name) => {
            if (!name) {
                return;
            }

            const query = JSON.stringify({ name });
            assignLocation(`/ipm/v1c/testTypesExport/exportJSON?t=${Date.now()}&query=${encodeURIComponent(query)}`);
        },
        [assignLocation]
    );

    const buildEditUrl = useCallback((testTypeName) => {
        const baseUrl = buildRootPageTarget(AdminPages.ServiceTests);
        const encoded = encodeURIComponent(testTypeName || '');
        return encoded ? `${baseUrl}/${encoded}` : baseUrl;
    }, []);

    const buildCopyUrl = useCallback(
        (testTypeName) => {
            const editUrl = buildEditUrl(testTypeName);
            const params = new URLSearchParams();
            params.set('copy', 'copy');
            return `${editUrl}?${params.toString()}`;
        },
        [buildEditUrl]
    );

    // Like portal/ui: list.testTypes | groupBy:'group'
    const groupedTestTypes = useMemo(() => groupTestTypes(testTypes), [testTypes]);

    if (!initialized) {
        return <LinearProgress />;
    }

    if (groupedTestTypes.length < 1) {
        return <Typography className={classes.cardListContainer}>{t`No service test types found`}</Typography>;
    }

    return (
        <div className={classes.cardListContainer}>
            <TestTypeDeleteDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
            />
            {groupedTestTypes.map(({ group, testTypes: groupTypes }) => {
                // Like portal/ui: list.groupCounts[group]
                const counts = groupCounts[group] || {};
                const allCount = counts.all || 0;

                const firstType = groupTypes[0];
                const icon = groupTypes.length === 1 ? firstType?.details?.icon : null;
                const title = buildCardTitle(groupTypes, group);
                const description = buildDescription(groupTypes, group);
                const showEdit = canEdit(isAdminUser, groupTypes);
                const showDelete = canDelete(isAdminUser, groupTypes);
                const showCopy = canCopy(isAdminUser, groupTypes);
                const showExport = canExport(isAdminUser, groupTypes);
                const hasMultipleTypes = groupTypes.length > 1;

                return (
                    <div className={classes.cardListItem} key={group}>
                        <Card className={classes.card}>
                            <CardHeader
                                title={
                                    <div className={classes.cardHeaderTitle}>
                                        {icon && <MaterialUiIcon icon={icon} />}
                                        <div className={classes.cardHeaderTextWrap}>
                                            <Typography variant="h6" component="span" className={classes.cardTitleText}>
                                                {title}
                                            </Typography>
                                            <Badge
                                                className={classes.cardBadge}
                                                color="secondary"
                                                badgeContent={allCount}
                                                max={9999}
                                                showZero
                                            >
                                                <span className={classes.cardBadgeAnchor} />
                                            </Badge>
                                        </div>
                                    </div>
                                }
                            />
                            <CardContent className={classes.grow}>
                                {!hasMultipleTypes || groupTypes?.[0]?.builtIn === true ? (
                                    <Typography variant="body2" className={classes.cardDescription}>
                                        {description}
                                    </Typography>
                                ) : (
                                    groupTypes.map((testType) => {
                                        const itemShowEdit = canEdit(isAdminUser, [testType]);
                                        const itemShowDelete = canDelete(isAdminUser, [testType]);
                                        const itemShowCopy = canCopy(isAdminUser, [testType]);
                                        const itemShowExport = canExport(isAdminUser, [testType]);

                                        return (
                                            <div
                                                className={classes.supportingText}
                                                key={testType?._id || testType?.name}
                                            >
                                                <div className="subTitle">
                                                    {testType?.details?.icon && (
                                                        <i className="material-icons">{testType.details.icon}</i>
                                                    )}
                                                    {testType?.details?.title || testType?.name}

                                                    <div>
                                                        {itemShowEdit && (
                                                            <ActionIconButton
                                                                to={buildEditUrl(testType?.name)}
                                                                label={t`Edit`}
                                                            >
                                                                <Edit />
                                                            </ActionIconButton>
                                                        )}
                                                        {itemShowDelete && (
                                                            <ActionIconButton
                                                                onClick={() => handleDelete(testType?._id)}
                                                                label={t`Delete`}
                                                            >
                                                                <Delete />
                                                            </ActionIconButton>
                                                        )}
                                                        {itemShowCopy && (
                                                            <ActionIconButton
                                                                to={buildCopyUrl(testType?.name)}
                                                                label={t`Copy`}
                                                            >
                                                                <ContentCopy />
                                                            </ActionIconButton>
                                                        )}
                                                        {itemShowExport && (
                                                            <ActionIconButton
                                                                onClick={() => handleExport(testType?.name)}
                                                                label={t`Export`}
                                                            >
                                                                <GetApp />
                                                            </ActionIconButton>
                                                        )}
                                                    </div>
                                                </div>
                                                <Typography key={`${testType?._id}`} variant="body2">
                                                    {testType?.details?.description}
                                                </Typography>
                                            </div>
                                        );
                                    })
                                )}
                            </CardContent>
                            {firstType?.scriptConvertErr && (
                                <div className={classes.convertedScriptMessage}>
                                    <Warning className={classes.convertedScriptMessageIcon} />
                                    <Typography
                                        variant="body2"
                                        color="inherit"
                                        className={classes.convertedScriptMessageText}
                                    >
                                        {t`Error Converting script to Python 3`}
                                    </Typography>
                                </div>
                            )}
                            <CardActions disableSpacing className={classes.cardActions}>
                                <div className={classes.actions}>
                                    {showEdit && (
                                        <ActionIconButton
                                            className={classes.actionButton}
                                            to={buildEditUrl(firstType?.name)}
                                            label={t`Edit`}
                                        >
                                            <Edit />
                                        </ActionIconButton>
                                    )}
                                    {showDelete && (
                                        <ActionIconButton
                                            className={classes.actionButton}
                                            onClick={() => handleDelete(firstType?._id)}
                                            label={t`Delete`}
                                        >
                                            <Delete />
                                        </ActionIconButton>
                                    )}
                                    {showCopy && (
                                        <ActionIconButton
                                            className={classes.actionButton}
                                            to={buildCopyUrl(firstType?.name)}
                                            label={t`Copy`}
                                        >
                                            <ContentCopy />
                                        </ActionIconButton>
                                    )}
                                    {showExport && (
                                        <ActionIconButton
                                            className={classes.actionButton}
                                            onClick={() => handleExport(firstType?.name)}
                                            label={t`Export`}
                                        >
                                            <GetApp />
                                        </ActionIconButton>
                                    )}
                                </div>
                                <div className={classes.grow} />
                                <Button
                                    component={Link}
                                    to={buildGotoServiceTestsUrl(group)}
                                    // size="small"
                                    classes={{
                                        root: classes.gotoButton,
                                        label: classes.gotoButtonLabel,
                                        endIcon: classes.gotoButtonEndIcon,
                                    }}
                                    endIcon={<ArrowForward />}
                                >
                                    <span className={classes.gotoButtonText}>{t`Go To ${title}`}</span>
                                </Button>
                            </CardActions>
                        </Card>
                    </div>
                );
            })}
        </div>
    );
}
